package pl.mothdigital.neuronowi.pages.specialists

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.*
import com.varabyte.kobweb.silk.components.graphics.*
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.layout.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.keywords.*
import org.jetbrains.compose.web.dom.*
import pl.mothdigital.neuronowi.common.model.SpecialistData
import pl.mothdigital.neuronowi.common.resources.*
import pl.mothdigital.neuronowi.components.*
import pl.mothdigital.neuronowi.components.layouts.*
import pl.mothdigital.neuronowi.components.sections.cta.*
import pl.mothdigital.neuronowi.components.sections.specialist.*
import pl.mothdigital.neuronowi.components.text.*
import pl.mothdigital.neuronowi.common.utils.fixPolishOrphans
import pl.mothdigital.neuronowi.components.widgets.*
import pl.mothdigital.neuronowi.theme.*
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import org.jetbrains.compose.web.css.FlexWrap
import com.varabyte.kobweb.compose.css.AlignItems

private val priceItems = listOf(
    PriceItem(
        StringResources.EWA_SOFINSKA_PRICE_1_NAME,
        StringResources.EWA_SOFINSKA_PRICE_1_TIME,
        StringResources.EWA_SOFINSKA_PRICE_1_AMOUNT
    ),
    PriceItem(
        StringResources.EWA_SOFINSKA_PRICE_2_NAME,
        StringResources.EWA_SOFINSKA_PRICE_2_TIME,
        StringResources.EWA_SOFINSKA_PRICE_2_AMOUNT
    ),
    PriceItem(
        StringResources.EWA_SOFINSKA_PRICE_3_NAME,
        StringResources.EWA_SOFINSKA_PRICE_3_TIME,
        StringResources.EWA_SOFINSKA_PRICE_3_AMOUNT
    ),
    PriceItem(
        StringResources.EWA_SOFINSKA_PRICE_4_NAME,
        StringResources.EWA_SOFINSKA_PRICE_4_TIME,
        StringResources.EWA_SOFINSKA_PRICE_4_AMOUNT
    ),
    PriceItem(
        StringResources.EWA_SOFINSKA_PRICE_5_NAME,
        StringResources.EWA_SOFINSKA_PRICE_5_TIME,
        StringResources.EWA_SOFINSKA_PRICE_5_AMOUNT
    )
)

@Page
@Composable
fun EwaSofinskaPage() {
    SpecialistPageLayout(
        title = StringResources.EWA_SOFINSKA_PAGE_TITLE,
        headerSection = { HeaderSection() },
        profileSection = { ProfileSection() },
        specializationsSection = { SpecializationsSection() },
        servicesSection = { ServicesSection() },
        priceSection = { PriceSection() },
        ctaSection = { context ->
            CTASection(
                ctx = context,
                customAction = { js("window.scrollTo({ top: 0, behavior: 'smooth' })") },
            )
        }
    )
}

@Composable
private fun HeaderSection() {
    SpecialistHeaderSection(
        name = StringResources.EWA_SOFINSKA,
        titles = StringResources.EWA_SOFINSKA_ACADEMIC_TITLES,
        phoneNumber = StringResources.EWA_SOFINSKA_PHONE_NUMBER
    )
}

@Composable
private fun ProfileSection() {
    SpecialistProfileSection(
        avatar = ImageResources.avatarEwaSofinska,
        aboutParagraphs = listOf(
            StringResources.EWA_SOFINSKA_ABOUT_ME_1,
            StringResources.EWA_SOFINSKA_ABOUT_ME_2,
            StringResources.EWA_SOFINSKA_ABOUT_ME_3
        ),
        inviteMessage = StringResources.EWA_SOFINSKA_ABOUT_ME_4,
        imagePosition = ImagePosition.LEFT
    )
}

@Composable
private fun SpecializationsSection() {
    SpecializationsGridSection(
        specializations = listOf(
            SpecialistData(
                icon = { FaHandsHolding() },
                title = "Pedagogika specjalna",
                description = "Wsparcie edukacyjne i terapeutyczne dla dzieci ze specjalnymi potrzebami edukacyjnymi."
            ),
            SpecialistData(
                icon = { FaBrain() },
                title = "Integracja sensoryczna",
                description = "Diagnoza i terapia zaburzeń przetwarzania sensorycznego, pomagające dzieciom w lepszym odbiorze i interpretacji bodźców."
            ),
            SpecialistData(
                icon = { FaHands() },
                title = "Terapia ręki",
                description = "Rozwijanie sprawności manualnej, koordynacji wzrokowo-ruchowej i umiejętności grafomotorycznych."
            ),
            SpecialistData(
                icon = { FaChild() },
                title = "Wsparcie rozwoju",
                description = "Stymulacja ogólnorozwojowa dla dzieci z opóźnieniami rozwojowymi i specyficznymi trudnościami."
            )
        )
    )
}

@Composable
private fun ServicesSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .toAttrs()
    ) {
        Column(modifier = Modifier.fillMaxWidth().margin { auto }.gap(2.cssRem)) {
            AppH2 { Text("Metody pracy") }

            AppP(modifier = Modifier.textAlign(TextAlign.Justify)) {
                Text(
                    """
                    W swojej pracy wykorzystuję różnorodne metody dostosowane do indywidualnych potrzeb każdego dziecka. 
                    Prowadzę kompleksową diagnozę, która pozwala mi zidentyfikować obszary wymagające wsparcia i opracować 
                    spersonalizowany plan terapeutyczny.
                """.trimIndent().fixPolishOrphans()
                )
            }

            SimpleGrid(
                modifier = Modifier.fillMaxWidth().gap(1.cssRem),
                numColumns = numColumns(base = 1, sm = 1, md = 2, lg = 2, xl = 2)
            ) {
                Column(modifier = Modifier.gap(0.5.cssRem)) {
                    AppH3 { Text("Techniki terapeutyczne") }
                    Ul {
                        Li { Text("Diagnoza i terapia integracji sensorycznej".fixPolishOrphans()) }
                        Li { Text("Ćwiczenia grafomotoryczne".fixPolishOrphans()) }
                        Li { Text("Metoda Dennisona (Kinezjologia Edukacyjna)".fixPolishOrphans()) }
                        Li { Text("Stymulacja polisensoryczna".fixPolishOrphans()) }
                    }
                }

                Column(modifier = Modifier.gap(0.5.cssRem)) {
                    AppH3 { Text("Grupy pacjentów") }
                    Ul {
                        Li { Text("Dzieci z zaburzeniami integracji sensorycznej".fixPolishOrphans()) }
                        Li { Text("Dzieci z trudnościami w nauce".fixPolishOrphans()) }
                        Li { Text("Dzieci z trudnościami w pisaniu i koordynacji ruchowej".fixPolishOrphans()) }
                        Li { Text("Dzieci ze specjalnymi potrzebami edukacyjnymi".fixPolishOrphans()) }
                    }
                }
            }
        }
    }
}

@Composable
private fun PriceSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .backgroundColor(SiteTheme.green._50.toRgb().copy(alpha = 128))
            .toAttrs()
    ) {
        Column(modifier = Modifier.fillMaxWidth().margin { auto }.gap(2.cssRem)) {
            AppH2 { Text(StringResources.EWA_SOFINSKA_PRICE_TITLE.fixPolishOrphans()) }

            Column(modifier = Modifier.gap(0.5.cssRem)) {
                AppP(modifier = Modifier.fontSize(0.9.cssRem).margin(bottom = 0.px)) { Text(StringResources.EWA_SOFINSKA_PRICE_HEADER_1.fixPolishOrphans()) }
                AppP(modifier = Modifier.fontSize(0.9.cssRem).margin(bottom = 0.px)) { Text(StringResources.EWA_SOFINSKA_PRICE_HEADER_2.fixPolishOrphans()) }
                AppP(modifier = Modifier.fontSize(0.9.cssRem).margin(bottom = 1.cssRem)) { Text(StringResources.EWA_SOFINSKA_PRICE_HEADER_3.fixPolishOrphans()) }
            }

            SpecialistPriceTable(priceItems = priceItems)

            AppP(modifier = Modifier.fontSize(0.9.cssRem), color = SiteTheme.black._700) {
                Text(StringResources.EWA_SOFINSKA_PRICE_NOTE.fixPolishOrphans())
            }
        }
    }
}
